import { Link } from '@carto/meridian-ds/components'
import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import { useAppStore } from 'app/state/hooks'
import { FileType, download } from 'app/utils/download'
import { createDialogOpener } from 'features/dialogs/openDialog'
import { getLocalExportData } from 'features/builder/model/localExportsStore'
import { JobStatusType, ExportJob, JobType, LocalLayerDataExportJob, ActivityDataExportJob } from 'features/jobs/types'
import { cancelExportJob } from 'features/jobs/models/exportsModel'
import useNotifications from 'features/notifications/ui/useNotifications'
import JobItem from '../JobItem'
import { isExportableJob } from '../../../utils'

const openErrorDetailsDialog = createDialogOpener(() => import('../common/JobErrorDetailsDialog'))
const openJobCancelDialog = createDialogOpener(() => import('../common/JobCancelDialog'))
const openLayerExportDownloadsDialog = createDialogOpener(() => import('./LayerExportDownloadsDialog'))
const openActivityDataExportDownloadsDialog = createDialogOpener(() => import('./ActivityDataExportDownloadsDialog'))

interface ExportTaskItemProps {
  job: ExportJob | LocalLayerDataExportJob | ActivityDataExportJob
}

const getExportType = (type: JobType) => {
  if (type === JobType.ActivityData) {
    return 'activityDataExport'
  }

  if (type === JobType.WorkflowDataExport) {
    return 'workflowDataExport'
  }

  return 'layerExport'
}

function ExportTaskItem({ job }: ExportTaskItemProps) {
  const intl = useIntl()
  const store = useAppStore()
  const { sendNeutralNotificationLocalize, sendErrorNotificationLocalize } = useNotifications()

  const status = job.status || JobStatusType.Running
  const exportType = getExportType(job.type)

  const { message, tooltip } = useMemo(() => {
    switch (status) {
      case JobStatusType.Success:
        return {
          message: intl.formatMessage(
            { id: `jobs.${exportType}.status.success` },
            { downloadLink: (content: React.ReactNode) => <Link>{content}</Link> }
          ),
          tooltip: intl.formatMessage({ id: `jobs.export.tooltip.success` })
        }
      case JobStatusType.Failure:
        return {
          message: intl.formatMessage(
            { id: `jobs.${exportType}.status.failure` },
            {
              readMoreLink: (content: React.ReactNode) => <Link>{content}</Link>
            }
          ),
          tooltip: intl.formatMessage({ id: 'jobs.tooltip.error' })
        }
      default: // JobStatusType.Pending, JobStatusType.Running
        return {
          message: intl.formatMessage({ id: `jobs.${exportType}.status.running` }),
          tooltip: intl.formatMessage({ id: 'jobs.tooltip.running' })
        }
    }
  }, [exportType, status, intl])

  const handleClick = async () => {
    if (!job.status) return

    if (job.status === JobStatusType.Success) {
      if (job.type === JobType.LocalLayerDataExport) {
        const dataAsCsv = getLocalExportData(job.id)

        if (!dataAsCsv) {
          sendErrorNotificationLocalize('builder.actions.export.noDataInLayer')
          return
        }

        download(`${job.name}.csv`, {
          content: dataAsCsv,
          type: FileType.Csv
        })
      } else if (isExportableJob(job.type)) {
        openLayerExportDownloadsDialog({ job: job as ExportJob })
      } else {
        openActivityDataExportDownloadsDialog({ job: job as ActivityDataExportJob })
      }
    } else if (job.status === JobStatusType.Failure) {
      openErrorDetailsDialog({
        title: intl.formatMessage({ id: `jobs.${exportType}.error.title` }),
        message: intl.formatMessage({ id: `jobs.${exportType}.error.message` }),
        error: ('remoteJobStatus' in job && job.remoteJobStatus?.errorMessage) || job.error?.msg
      })
    }
  }

  const handleCancelJob = () => {
    openJobCancelDialog({ job, cancelJob })
  }

  const cancelJob = async () => {
    try {
      await cancelExportJob({ store, job })
      sendNeutralNotificationLocalize(`jobs.${exportType}.cancel.notificationSuccess`)
    } catch (error) {
      sendErrorNotificationLocalize(`jobs.${exportType}.cancel.notificationFailed`)
      console.error('failed to cancel layer export job', error)
    }
  }

  if (!status) {
    return null
  }

  return (
    <JobItem
      status={status}
      name={job.name}
      message={message}
      tooltip={tooltip}
      onClick={handleClick}
      padded={true}
      onCancel={handleCancelJob}
    />
  )
}

export default ExportTaskItem
